import { useMemo, useState } from 'react'
import { ArrowLeft, ClipboardText, MagnifyingGlass, MapPin, Plus, Warehouse } from '@phosphor-icons/react'
import { Badge, Banner, Button, DataTable, EmptyState, Input, Select, type Column } from '@/ui'
import { istDateKey } from '@/lib/ist'
import { errorMessage } from '../api/core'
import { useVillages } from '../api/reference'
import {
  LOCATION_KIND_LABEL,
  LOCATION_KINDS,
  lookupLocationLabel,
  useCountRequests,
  useLocation,
  useLocationTree,
  useLocations,
  useOutOfDateBays,
  useSites,
  type LocationRow,
} from '../api/store'
import { ActionButton } from '../components/Can'
import { FilterBar, SearchFilter, SelectFilter } from '../components/FilterBar'
import { ConsolePage, DetailColumns, QueryState } from '../components/Layout'
import { LocationDrawer } from '../components/LocationDrawer'
import { LocationFormDialog, type LocationDialogMode } from '../components/LocationFormDialog'
import { LocationTeam } from '../components/LocationTeam'
import { LocationTree } from '../components/LocationTree'
import { Code, Facts, Section, UserName, When } from '../components/bits'
import { fssaiExpired } from '../model/locationForm'
import { flattenTree } from '../model/locationTree'
import { useUrlState } from '../lib/useUrlState'

const LIMIT = 50
const KIND_OPTIONS = LOCATION_KINDS.map((k) => ({ value: k, label: LOCATION_KIND_LABEL[k]! }))

/** Locations (F5.3-b, F5.4, F5.6-e): sites and the places in them, labels, contents and teams. */
export function LocationsPage() {
  const [f, setF, { reset, active }] = useUrlState({ kind: '', search: '', site: '', place: '', page: 1 })
  const [dialog, setDialog] = useState<{ key: number; mode: LocationDialogMode } | null>(null)
  const open = (mode: LocationDialogMode) => setDialog({ key: Date.now(), mode })

  return (
    <ConsolePage
      eyebrow="Store"
      title="Locations"
      description="Sites and the zones, bays and spots inside them, each with its printed label. Pick a place to see what is in it."
      actions={
        <ActionButton perm="locations:create" hide leadingIcon={<Plus />} onClick={() => open({ kind: 'site' })}>
          New site
        </ActionButton>
      }
    >
      <LabelFinder onFound={(loc) => setF({ site: loc.siteId ?? loc.id, place: loc.id })} />
      {f.site ? (
        <SiteView
          siteId={f.site}
          placeId={f.place || null}
          kind={f.kind}
          onSite={(site) => setF({ site, place: '' })}
          onPlace={(place) => setF({ place })}
          onBack={() => setF({ site: '', place: '' })}
          onDialog={open}
        />
      ) : (
        <SitesTable
          kind={f.kind}
          search={f.search}
          page={f.page}
          onChange={setF}
          onClear={reset}
          active={active}
          onPick={(site) => setF({ site, place: '' })}
        />
      )}
      {dialog && (
        <LocationFormDialog
          key={dialog.key}
          mode={dialog.mode}
          onClose={() => setDialog(null)}
          onSaved={(id) => {
            if (dialog.mode.kind === 'site') setF({ site: id, place: '' })
            else if (dialog.mode.kind === 'child') setF({ place: id })
          }}
        />
      )}
    </ConsolePage>
  )
}

function SitesTable({
  kind,
  search,
  page,
  onChange,
  onClear,
  active,
  onPick,
}: {
  kind: string
  search: string
  page: number
  onChange: (p: { kind?: string; search?: string; page?: number }) => void
  onClear: () => void
  active: boolean
  onPick: (id: string) => void
}) {
  const q = useLocations({ level: 'SITE', kind: kind || undefined, search: search || undefined, page, limit: LIMIT })
  const villages = useVillages()
  const villageName = useMemo(() => new Map((villages.data ?? []).map((v) => [v.id, v.name])), [villages.data])
  const columns: Column<LocationRow>[] = [
    {
      id: 'name',
      header: 'Site',
      cell: (r) => (
        <span className="flex flex-col">
          <span className="text-ink">{r.name}</span>
          <span className="text-[12.5px] text-muted">{r.groupId ? (villageName.get(r.groupId) ?? 'Village') : (LOCATION_KIND_LABEL[r.kind ?? ''] ?? '')}</span>
        </span>
      ),
      sortValue: (r) => r.name,
    },
    { id: 'code', header: 'Code', cell: (r) => <Code>{r.code}</Code>, sortValue: (r) => r.code },
    { id: 'kind', header: 'Kind', cell: (r) => LOCATION_KIND_LABEL[r.kind ?? ''] ?? '—', sortValue: (r) => r.kind, hideBelow: 'md' },
    { id: 'label', header: 'Label', cell: (r) => <Code className={r.labelPayload ? undefined : 'text-muted'}>{r.labelPayload ?? 'none'}</Code>, hideBelow: 'lg' },
    {
      id: 'fssai',
      header: 'FSSAI licence',
      cell: (r) => <FssaiLicence no={r.fssaiLicenceNo} validTo={r.fssaiLicenceValidTo} />,
      sortValue: (r) => r.fssaiLicenceNo,
      hideBelow: 'lg',
    },
    { id: 'items', header: 'Items', cell: (r) => r.itemsHere, sortValue: (r) => r.itemsHere, mono: true, align: 'right' },
    { id: 'active', header: 'Status', cell: (r) => (r.active ? <Badge tone="ok">In use</Badge> : <Badge>Not in use</Badge>), hideBelow: 'sm' },
  ]
  return (
    <DataTable<LocationRow>
      caption="Sites"
      columns={columns}
      rows={q.data?.rows ?? []}
      getRowId={(r) => r.id}
      loading={q.isLoading}
      error={q.isError ? errorMessage(q.error) : undefined}
      onRowClick={(r) => onPick(r.id)}
      pageSize={LIMIT}
      server={{ page, total: q.data?.total ?? 0, onPageChange: (p) => onChange({ page: p }) }}
      filters={
        <FilterBar onClear={onClear} active={active}>
          <SelectFilter label="Kind" value={kind} onChange={(k) => onChange({ kind: k })} options={KIND_OPTIONS} />
          <SearchFilter label="Search" value={search} onChange={(s) => onChange({ search: s })} placeholder="Code or name" />
        </FilterBar>
      }
      empty={
        <EmptyState
          icon={<Warehouse />}
          title={active ? 'No site matches' : 'No sites yet'}
          description={active ? 'Try another kind or search.' : 'Add the first warehouse or collection point with New site.'}
        />
      }
    />
  )
}

function SiteView({
  siteId,
  placeId,
  kind,
  onSite,
  onPlace,
  onBack,
  onDialog,
}: {
  siteId: string
  placeId: string | null
  kind: string
  onSite: (id: string) => void
  onPlace: (id: string) => void
  onBack: () => void
  onDialog: (mode: LocationDialogMode) => void
}) {
  const sites = useSites()
  const tree = useLocationTree(siteId)
  // The tree has no licence fields; the site's own record does.
  const siteDetail = useLocation(siteId)
  const outOfDate = useOutOfDateBays(siteId)
  const villages = useVillages()
  const nodes = useMemo(() => (tree.data ? flattenTree(tree.data) : []), [tree.data])
  const expiring = useMemo(
    () => new Set((outOfDate.data ?? []).filter((b) => b.state === 'EXPIRING').map((b) => b.bayId)),
    [outOfDate.data],
  )
  const site = tree.data
  const siteRow = sites.data?.find((s) => s.id === siteId)
  const node = placeId ? nodes.find((n) => n.row.id === placeId) : undefined
  const bay = node?.row.bayId ? (nodes.find((n) => n.row.id === node.row.bayId)?.row ?? null) : null
  const siteOptions = (sites.data ?? [])
    .filter((s) => !kind || s.kind === kind || s.id === siteId)
    .map((s) => ({ value: s.id, label: `${s.name} · ${s.code}` }))
  const village = siteRow?.groupId ? villages.data?.find((v) => v.id === siteRow.groupId)?.name : null

  return (
    <>
      <div className="flex flex-wrap items-end gap-2">
        <Button variant="ghost" size="sm" leadingIcon={<ArrowLeft />} onClick={onBack}>
          All sites
        </Button>
        <label className="flex min-w-[16rem] flex-col gap-1">
          <span className="font-mono text-[10px] font-bold uppercase tracking-label text-muted">Site</span>
          <Select aria-label="Site" value={siteId} onChange={onSite} options={siteOptions} className="h-9 text-[14px]" />
        </label>
      </div>
      <QueryState query={tree} label="the site">
        {site && (
          <DetailColumns
            main={
              <Section
                title={site.name}
                eyebrow="Places"
                actions={
                  <ActionButton perm="locations:create" hide size="sm" variant="secondary" leadingIcon={<Plus />} onClick={() => onDialog({ kind: 'child', parent: site })} disabled={!site.active}>
                    Add a place
                  </ActionButton>
                }
              >
                {nodes.length <= 1 ? (
                  <EmptyState icon={<MapPin />} title="No places inside yet" description="Add the zones and bays, then print their labels." />
                ) : (
                  <LocationTree nodes={nodes} selectedId={placeId} onSelect={onPlace} expiringBayIds={expiring} />
                )}
              </Section>
            }
            side={
              <>
                <Section title="Site" eyebrow={site.kind ? LOCATION_KIND_LABEL[site.kind] : undefined}>
                  <Facts
                    columns={2}
                    items={[
                      { label: 'Code', value: <Code>{site.code}</Code> },
                      { label: 'Label', value: <Code>{site.labelPayload ?? '—'}</Code> },
                      { label: 'Items', value: nodes[0]?.itemsBelow ?? 0, mono: true },
                      { label: 'Bays to declare', value: (outOfDate.data ?? []).length, mono: true },
                      {
                        label: 'FSSAI licence',
                        value: <FssaiLicence no={siteDetail.data?.fssaiLicenceNo ?? null} validTo={null} />,
                      },
                      {
                        label: 'Licence valid to',
                        value: <FssaiValidTo validTo={siteDetail.data?.fssaiLicenceValidTo ?? null} />,
                      },
                      ...(village ? [{ label: 'Village', value: village }] : []),
                      { label: 'Status', value: site.active ? 'In use' : 'Not in use' },
                    ]}
                  />
                  <Button variant="ghost" size="sm" className="mt-3" onClick={() => onPlace(site.id)}>
                    Open site details
                  </Button>
                </Section>
                <CountRequests siteId={site.id} onPlace={onPlace} />
                <LocationTeam siteId={site.id} siteName={site.name} />
              </>
            }
          />
        )}
      </QueryState>
      {node && (
        <LocationDrawer
          key={node.row.id}
          node={node}
          bay={bay}
          expiring={bay ? expiring.has(bay.id) : false}
          onClose={() => onPlace('')}
          onAddChild={(parent) => onDialog({ kind: 'child', parent })}
          onEdit={(row) =>
            onDialog({
              kind: 'edit',
              row:
                row.level === 'SITE' && siteDetail.data
                  ? {
                      ...row,
                      fssaiLicenceNo: siteDetail.data.fssaiLicenceNo,
                      fssaiLicenceValidTo: siteDetail.data.fssaiLicenceValidTo,
                    }
                  : row,
            })
          }
        />
      )}
    </>
  )
}

function FssaiLicence({ no, validTo }: { no: string | null; validTo: string | null }) {
  if (!no) return <span className="text-muted">None</span>
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Code>{no}</Code>
      {fssaiExpired(validTo, istDateKey()) && <Badge tone="danger">Expired</Badge>}
    </span>
  )
}

function FssaiValidTo({ validTo }: { validTo: string | null }) {
  if (!validTo) return <span className="text-muted">—</span>
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <When at={validTo} dateOnly />
      {fssaiExpired(validTo, istDateKey()) && <Badge tone="danger">Expired</Badge>}
    </span>
  )
}

/** Counts the office asked of this site's places; open until the keeper's count arrives. */
function CountRequests({ siteId, onPlace }: { siteId: string; onPlace: (id: string) => void }) {
  const [status, setStatus] = useState<'OPEN' | 'DONE'>('OPEN')
  const q = useCountRequests({ siteId, status, page: 1, limit: 20 })
  const rows = q.data?.rows ?? []
  return (
    <Section
      title="Counts asked"
      eyebrow={status === 'OPEN' ? `${q.data?.total ?? 0} open` : 'Recently counted'}
      actions={
        <div className="flex gap-1" role="group" aria-label="Show">
          {(['OPEN', 'DONE'] as const).map((v) => (
            <Button key={v} size="sm" variant={status === v ? 'secondary' : 'ghost'} aria-pressed={status === v} onClick={() => setStatus(v)}>
              {v === 'OPEN' ? 'Open' : 'Counted'}
            </Button>
          ))}
        </div>
      }
    >
      <QueryState query={q} label="the count requests">
        {rows.length === 0 ? (
          <p className="text-[14px] text-muted">
            {status === 'OPEN'
              ? 'No count is waiting. Ask for one from a place: Request a count.'
              : 'No asked count has been done yet.'}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-rule">
            {rows.map((r) => (
              <li key={r.id} className="flex items-start gap-2 py-2 first:pt-0 last:pb-0">
                <ClipboardText className="mt-0.5 shrink-0 text-muted" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => onPlace(r.locationId)}
                    className="text-left font-mono text-[13px] text-ink underline decoration-rule underline-offset-2 hover:decoration-amber"
                  >
                    {r.locationCode ?? r.path ?? 'Place'}
                  </button>
                  {r.note && <p className="text-[13.5px] text-ink">{r.note}</p>}
                  <p className="text-[12.5px] text-muted">
                    Asked <When at={r.requestedAt} relative />
                    {r.requestedById && (
                      <>
                        {' by '}
                        {r.requestedByName ?? <UserName id={r.requestedById} />}
                      </>
                    )}
                    {r.status === 'DONE' && (
                      <>
                        {' · counted '}
                        <When at={r.countedAt} relative />
                      </>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </QueryState>
    </Section>
  )
}

/** Type or scan a label (or the code under it) and jump to that place. */
function LabelFinder({ onFound }: { onFound: (loc: LocationRow) => void }) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const find = async () => {
    const payload = text.trim()
    if (!payload) return
    setBusy(true)
    setError(null)
    try {
      onFound(await lookupLocationLabel(payload))
      setText('')
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <form
      className="flex flex-wrap items-start gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        void find()
      }}
    >
      <div className="min-w-[16rem] flex-1 sm:max-w-md">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Find a label: scan or type it, or the code under it"
          aria-label="Find a label"
          leading={<MagnifyingGlass />}
          className="h-9 font-mono text-[14px]"
        />
      </div>
      <Button type="submit" size="sm" variant="secondary" className="h-9" loading={busy} disabled={!text.trim()}>
        Find
      </Button>
      {error && (
        <Banner tone="warn" className="w-full">
          {error}
        </Banner>
      )}
    </form>
  )
}
